import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { FaGithub } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const code = searchParams.get('code');

    if (errorParam) {
      console.error('OAuth error:', errorParam, errorDescription);
      setError(errorDescription || t('terminal.auth.errors.loginFailed'));
      window.history.replaceState({}, '', '/login');
      return;
    }

    if (code) {
      console.log('Found code in URL, handling OAuth callback...');
      handleOAuthCallback();
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signIn(formData.email, formData.password);
      navigate('/learn');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('terminal.auth.errors.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthCallback = async () => {
    setLoading(true);
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const storedState = sessionStorage.getItem('oauth_state');
    
    console.log('Handling OAuth callback:', { 
      code: code?.substring(0, 8) + '...', 
      state, 
      storedState,
      match: state === storedState 
    });

    try {
      // Clear stored state immediately to prevent reuse
      sessionStorage.removeItem('oauth_state');

      // Verify state
      if (!state || !storedState || state !== storedState) {
        throw new Error('Invalid OAuth state');
      }

      if (!code) {
        throw new Error('No code found in URL');
      }

      // Exchange the code for a session
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);

      if (error) throw error;

      if (data?.session) {
        // Clear URL parameters
        window.history.replaceState({}, '', '/login');
        navigate('/learn');
        return;
      }

      throw new Error('No session established');

    } catch (err) {
      console.error('OAuth callback error:', err);
      setError(err instanceof Error ? err.message : t('terminal.auth.errors.loginFailed'));
      // Clear URL parameters on error
      window.history.replaceState({}, '', '/login');
    } finally {
      setLoading(false);
    }
  };

  const handleGithubLogin = async () => {
    setError(null);
    setLoading(true);
    
    try {
      const state = crypto.randomUUID();
      sessionStorage.setItem('oauth_state', state);

      // Use Supabase's callback URL instead of our local URL
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
        options: {
          redirectTo: 'https://programe.tech/community'
        }
      })
      
      if (error) throw error;
      console.log('OAuth initialization successful:', data);
      
    } catch (err) {
      console.error('OAuth error:', err);
      setError(err instanceof Error ? err.message : t('terminal.auth.errors.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-base-100 flex items-center justify-center p-4 pt-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="max-w-md w-full">
        <div className="card bg-base-200 shadow-xl border border-primary/20">
          <div className="card-body">
            <h2 className="card-title text-primary font-cyberpunk text-2xl mb-6">{t('terminal.auth.login')}</h2>
            
            {error && (
              <div className="alert alert-error mb-4">
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-cyberpunk">{t('terminal.auth.email')}</span>
                </label>
                <input
                  type="email"
                  placeholder={t('terminal.auth.emailPlaceholder')}
                  className="input input-bordered w-full"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-cyberpunk">{t('terminal.auth.password')}</span>
                </label>
                <input
                  type="password"
                  placeholder={t('terminal.auth.passwordPlaceholder')}
                  className="input input-bordered w-full"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
                <label className="label">
                  <Link 
                    to="/reset-password" 
                    className="label-text-alt link link-primary"
                  >
                    {t('terminal.auth.forgotPassword')}
                  </Link>
                </label>
              </div>

              <button
                type="submit"
                className={`btn btn-primary w-full font-cyberpunk ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? t('terminal.auth.loggingIn') : t('terminal.auth.login')}
              </button>
            </form>

            <div className="divider">{t('common.or')}</div>

            <div className="flex flex-col gap-2">
              <button
                onClick={handleGithubLogin}
                className="btn btn-outline w-full font-cyberpunk gap-2"
                disabled={loading}
              >
                <FaGithub size={20} />
                {t('terminal.auth.loginWithGithub')}
              </button>

              <Link
                to="/register"
                className="btn btn-outline btn-primary w-full font-cyberpunk"
              >
                {t('terminal.auth.createAccount')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
